// src/pages/articles/ArticleVancouverBubbleTea.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverBubbleTea() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-bubble-tea-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-bubble-world": "Bubble World",
    "section-boba-run": "Boba Run",
    "section-xing-fu-tang": "Xing Fu Tang",
    "section-the-alley": "The Alley",
    "section-baroness": "Baroness",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/green-vancouver/single-use-items.aspx",
      label: "City of Vancouver – Single-Use Item Reduction Strategy",
    },
    {
      url: "https://vanbubbleteafest.ca",
      label: "Canada Bubble Tea Festival (Vancouver)",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Dining Guides",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // NOTE: The "id" field must match the section ref, and lat/lng must match the <Marker> usage below.
  // Here we've assigned each bubble tea shop its own ID/coordinates, plus any new ones if needed.
  const locations = [
    {
      id: "section-bubble-world",
      lat: 49.2852,
      lng: -123.1303,
      popupText: "Bubble World - 1325 Robson St, Vancouver",
    },
    {
      id: "section-boba-run",
      lat: 49.2826,
      lng: -123.1087,
      popupText: "Boba Run - 102 W Hastings St, Vancouver",
    },
    {
      id: "section-xing-fu-tang",
      lat: 49.2583,
      lng: -123.1151,
      popupText: "Xing Fu Tang - 3432 Cambie St, Vancouver",
    },
    {
      id: "section-the-alley",
      lat: 49.2810,
      lng: -123.1258,
      popupText: "The Alley - 985 Hornby St, Vancouver",
    },
    {
      id: "section-baroness",
      lat: 49.2640,
      lng: -123.1680,
      popupText: "Baroness - 2790 W Broadway, Vancouver",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>5 Must-Try Bubble Tea Shops in Vancouver</title>
        <meta
          name="description"
          content="Explore Vancouver's vibrant bubble tea scene with these 5 must-try boba spots. Tips on local regulations, sustainability, and FAQs included!"
        />
        <meta
          name="keywords"
          content="Bubble tea, Vancouver, Boba, Milk tea, Travel tips, Food guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-bubble-tea-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="5 Must-Try Bubble Tea Shops in Vancouver"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's vibrant bubble tea scene with these 5 must-try boba spots. Includes insider tips, local regulations, and FAQs."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-bubble-tea-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-bubble-tea-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="5 Must-Try Bubble Tea Shops in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's vibrant bubble tea scene with these 5 must-try boba spots. Includes insider tips, local regulations, and FAQs."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-bubble-tea-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Bubble tea cups"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s bubble tea scene is one of the most vibrant in North
              America. From authentic Taiwanese classics to health-conscious
              vegan creations, the city offers a rich and diverse array of boba
              options. Whether you’re a first-time visitor or a seasoned local
              looking for the next trendy spot, exploring Vancouver’s bubble tea
              landscape is a must. This guide highlights five standout shops,
              each delivering a unique experience—complete with addresses, tips,
              coordinates, and more. Let’s dive in and discover why bubble tea
              in Vancouver is such a delicious adventure!
            </p>

            {/* SECTION: Bubble World */}
            <h2 id="section-bubble-world">1. Bubble World – The Classic</h2>
            <p>
              <strong>Bubble World</strong> is a longstanding favorite among
              Vancouver’s boba enthusiasts. Founded in the 1990s, this shop
              introduced many locals to authentic Taiwanese milk tea and has
              maintained its popularity with consistently chewy pearls and
              strong, robust brews. The Robson Street location is especially
              notable for its late-night hours, making it a convenient stop for
              after-dinner cravings.
            </p>
            <p>
              <em>Specialties:</em> Classic Honey Green Milk Tea, real fruit
              slushes, taro milk tea. Most drinks cost between $5–$7.
            </p>
            <p>
              <em>Dietary Notes:</em> Non-dairy creamer is used in milk teas
              (contains sodium caseinate). Fruit-based drinks are typically
              dairy-free.
            </p>
            <p
              ref={(el) => setSpotRef("section-bubble-world", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1325 Robson St, Vancouver, BC V6E 1C6{" "}
              <Marker
                coordY={49.2852}
                coordX={-123.1303}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Boba Run */}
            <h2 id="section-boba-run">2. Boba Run – Budget-Friendly & Fresh</h2>
            <p>
              <strong>Boba Run</strong> offers high-quality bubble tea at
              wallet-friendly prices, focusing on fresh ingredients like organic
              cane sugar and loose-leaf teas brewed to order. Located near
              Gastown, it attracts students, office workers, and curious
              passersby with a modern vibe and Korean-inspired flavors. Many
              drinks are easily customizable with plant-based milks, ensuring
              vegan options abound.
            </p>
            <p>
              <em>Specialties:</em> Vietnamese Coffee Milk Tea, Matcha Strawberry
              Latte, Dalgona Milk Tea. Expect prices mostly in the $5–$6 range.
            </p>
            <p>
              <em>Dietary Notes:</em> Vegan options using oat or almond milk,
              gluten-free tapioca pearls, and organic sweeteners.
            </p>
            <p
              ref={(el) => setSpotRef("section-boba-run", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 102 W Hastings St, Vancouver, BC V6B
              1G8{" "}
              <Marker
                coordY={49.2826}
                coordX={-123.1087}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Xing Fu Tang */}
            <h2 id="section-xing-fu-tang">3. Xing Fu Tang – Brown Sugar Craze</h2>
            <p>
              <strong>Xing Fu Tang</strong> is the shop credited with fueling
              Vancouver’s brown sugar boba phenomenon. Originating from Taiwan,
              they prepare caramelized pearls in a wok right before your eyes,
              creating a rich, indulgent taste. The Cambie Street location
              continues to draw crowds seeking that signature swirl of smoky
              brown sugar in fresh milk.
            </p>
            <p>
              <em>Specialties:</em> Brown Sugar Pearl Milk, Brown Sugar Matcha
              Latte, and seasonal fruit-based jelly drinks. Prices typically
              range from $6–$7.
            </p>
            <p>
              <em>Dietary Notes:</em> Primarily dairy-based drinks; fruit teas
              for lactose-intolerant customers. Sugar content is generally high,
              but sweetness levels can sometimes be adjusted.
            </p>
            <p
              ref={(el) => setSpotRef("section-xing-fu-tang", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 3432 Cambie St, Vancouver, BC V5Z 2W8{" "}
              <Marker
                coordY={49.2583}
                coordX={-123.1151}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: The Alley */}
            <h2 id="section-the-alley">4. The Alley – Mid-Range Artisan Teas</h2>
            <p>
              <strong>The Alley</strong> combines stylish decor and innovative
              flavor profiles. Known for its “Deerioca” tapioca pearls and
              sugar-cane syrup made in-house, this Taiwanese chain offers a
              mid-range option for those who want more than a basic milk tea but
              without venturing into premium price territory. Its Royal No.9
              Milk Tea—lightly infused with a berry aroma—remains a best-seller.
            </p>
            <p>
              <em>Specialties:</em> Royal No.9 Milk Tea, Brown Sugar Deerioca
              Fever, Aurora Series fruit teas. Drinks average $6–$7.
            </p>
            <p>
              <em>Dietary Notes:</em> Soy or lactose-free milk substitutions
              available, gluten-free tapioca pearls.
            </p>
            <p
              ref={(el) => setSpotRef("section-the-alley", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 985 Hornby St, Vancouver, BC V6Z 3G5{" "}
              <Marker
                coordY={49.2810}
                coordX={-123.1258}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Baroness */}
            <h2 id="section-baroness">5. Baroness – Premium & Vegan-Friendly</h2>
            <p>
              <strong>Baroness</strong> stands out for its premium ingredients
              and health-focused approach. With oat milk as the default in most
              drinks, this shop is exceptionally vegan-friendly. Brown sugar
              pearls slow-cooked in unrefined sugar and innovative flavors like
              Ube Brown Sugar Latte set Baroness apart. It may be slightly more
              expensive, but the unique, plant-based menu appeals to those with
              dietary restrictions or anyone seeking a guilt-free treat.
            </p>
            <p>
              <em>Specialties:</em> Brown Sugar Oat Milk with Pearls, Ube Brown
              Sugar Latte, Thai Oat Milk Tea. Drinks are $6–$8.
            </p>
            <p>
              <em>Dietary Notes:</em> Largely vegan, dairy-free, and often
              gluten-free. Uses oat milk as the default—rare among bubble tea
              shops.
            </p>
            <p
              ref={(el) => setSpotRef("section-baroness", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 2790 W Broadway, Vancouver, BC V6K 3Z2{" "}
              <Marker
                coordY={49.2640}
                coordX={-123.1680}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Plastic Straw Ban:</strong> Vancouver has a ban on most
              single-use plastic items, including plastic straws. Bubble tea
              shops now use paper or biodegradable straws. If you prefer
              something sturdier, bring your own reusable wide straw.
            </p>
            <p>
              <strong>Disposable Cup Fee:</strong> The city requires businesses
              to charge a $0.25 fee for single-use cups. Bringing your own
              reusable cup helps you save money and reduce waste.
            </p>
            <p>
              <strong>Food Safety:</strong> All establishments follow Vancouver
              Coastal Health guidelines. If you have allergies or dietary
              restrictions, be sure to communicate with the staff—most shops are
              accustomed to making customized drinks.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. How much does bubble tea cost in Vancouver?</h3>
              <p>
                Expect to pay around $5–$7 for most drinks. Premium or specialty
                options can be closer to $8–$9.
              </p>

              <h3>2. Are there vegan options available?</h3>
              <p>
                Absolutely! Many shops offer dairy-free alternatives like oat,
                almond, or soy milk. Baroness, for instance, uses oat milk by
                default, making most of its menu vegan-friendly.
              </p>

              <h3>3. Why am I charged $0.25 extra for my cup?</h3>
              <p>
                Vancouver by-laws require a disposable cup fee to encourage the
                use of reusable cups. Bring your own cup to avoid the fee.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re seeking classic Taiwanese milk tea, a budget
              boba-fix, or an artisanal oat-milk creation, Vancouver has a
              bubble tea shop to suit every taste. These five must-try spots
              highlight the city’s vibrant boba culture—and there’s so much more
              to explore beyond this list. Remember to check local regulations
              regarding single-use plastics and cup fees, and feel free to bring
              a reusable straw or cup to sip sustainably. Now that you have the
              inside scoop, go forth and enjoy Vancouver’s bubble tea scene!
            </p>

            <p style={{ fontStyle: "italic", fontSize: "0.9em" }}>
              Disclaimer: Always check official resources or contact local
              authorities for the latest regulations and guidelines. Enjoy
              responsibly.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}